import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRangeInlineComponent from 'webapp/components/form/range/inline';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { ClientMetric } from 'webapp/models-custom';
import DateRange from 'webapp/models-custom/date-range';
import MetricLimitations from 'webapp/models-custom/metric-limitations';

interface Args {
	model: ClientMetric | null;
	position: number;
}

export default class ClientsMetricsMainComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/client-metrics')
	declare mmClientMetricsService: Service.ModelManager.ClientMetrics;
	@service('model-manager/metrics')
	declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked limitationsCM: MetricLimitations | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const model = this.args.model;
		if (model) {
			this.limitationsCM = new MetricLimitations({
				metrics: model.metricSetup.metricData ? [model.metricSetup.metricData] : [],
				isNumber: true,
				dateRange: DateRange.createFromFormula(model.dateRange),
			});
		}
	}

	get metricData() {
		const key = this.args.model?.metricSetupKey;
		let metricData: { name: string; avatar: string } | null = null;

		if (key) {
			const metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(key, null);

			if (metric) {
				metricData = {
					name: metric.name,
					avatar: metric.sourceAvatar,
				};
			}
		}

		return metricData;
	}

	get metricFullName() {
		const metricAttribute: string = this.args.model?.attributeName ?? '';
		let text = this.metricData?.name || '';

		if (metricAttribute) {
			text += ' / ' + metricAttribute;
		}

		return text;
	}

	get canEdit() {
		return this.currentUserService.isAdmin;
	}

	get isDateRangeDisabled() {
		return !this.canEdit;
	}

	get showInfoTooltip() {
		return !this.currentUserService.isDemo && !this.canEdit && !this.metricData;
	}

	get metricColumnAction() {
		return this.metricData ? 'openIntervalDropdown' : 'editFavorite';
	}

	@action
	updateDateRangeOnOutsideChange() {
		const model = this.args.model;
		if (model) {
			this.limitationsCM?.updateDateRangeFromFormula(model.dateRange);
		}
	}

	@action
	edit(hideOptionToSelectDimensions = false) {
		if (this.canEdit && !this.currentUserService.handleDemo()) {
			const model = this.args.model;
			this.mmClientMetricsService.openClientMetricsPopup({
				model: model
					? this.mmClientMetricsService.createModelFromRecord(model.model)
					: null,
				position: this.args.position,
				spaceId: this.accountService.id,
				hideOptionToSelectDimensions,
			});
		}
	}

	@action
	dateRangeChanged(selectedDateRange: DateRange) {
		if (!this.currentUserService.handleDemo()) {
			const model = this.args.model;

			if (model) {
				model.dateRange = selectedDateRange.formula;
				model
					.save()
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			}

			this.limitationsCM?.updateDateRangeFromOnlyRangeFormula(selectedDateRange.rangeFormula);
		}
	}
	<template>
		<li class="fav" {{didUpdate this.updateDateRangeOnOutsideChange @model.dateRange}}>
			<div class="ct {{unless this.canEdit 'no-edit'}}">
				{{#if this.metricData}}
					<div role="button" class="text" {{on "click" (fn this.edit false)}}>
						<img src={{this.metricData.avatar}} alt />
						<div class="title">
							{{this.metricFullName}}
							<ToolTipComponent @text={{this.metricFullName}} />
						</div>
					</div>
					{{#if this.limitationsCM}}
						<FormRangeInlineComponent
							@metricLimitations={{this.limitationsCM}}
							@onChange={{this.dateRangeChanged}}
							@labelText=""
							@isDisabled={{this.isDateRangeDisabled}}
							@addEm={{true}}
						/>
					{{/if}}
				{{else}}
					<a class={{unless this.metricData "empty"}} {{on "click" (fn this.edit true)}}>
						Set metric for all
						<CommonUiIconComponent
							@name={{if this.showInfoTooltip "InfoFill" "ChevronDown"}}
							@size="sm"
						/>
						{{#if this.showInfoTooltip}}
							<ToolTipComponent
								@text="Only admins can setup account metrics."
								@theme="light"
								@position="bottom"
								@maxWidth={{190}}
							/>
						{{/if}}
					</a>
				{{/if}}
			</div>
		</li>
	</template>
}
